import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Suppress postMessage cloning errors from browser extensions / platform scripts
// These errors don't affect functionality but appear in console
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const msg = args[0]?.toString() || '';
    if (msg.includes('postMessage') || msg.includes('could not be cloned')) {
      console.debug('[Suppressed] postMessage cloning error:', ...args);
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import PipelineRunsPage from "./pages/PipelineRunsPage";
import FundExplorerPage from "./pages/FundExplorerPage";
import FundPerformancePage from "./pages/FundPerformancePage";
import QuarterExplorerPage from "./pages/QuarterExplorerPage";
import BuySellPage from "./pages/BuySellPage";
import CompanyProfilesPage from "./pages/CompanyProfilesPage";
import StockPerfPage from "./pages/StockPerfPage";
import ConsensusPage from "./pages/ConsensusPage";
import AlphaPage from "./pages/AlphaPage";
import BarraPage from "./pages/BarraPage";
import SkillsPage from "./pages/SkillsPage";
import KensysTargetsPage from "./pages/KensysTargetsPage";
import NowcastingPage from "./pages/NowcastingPage";
import EntryAnchorPage from "./pages/EntryAnchorPage";
import KfmMockupPage from "./pages/KfmMockupPage";
import HiddenGemsPage from "./pages/HiddenGemsPage";
import LSBookPage from "./pages/LSBookPage";
import StrategyLabPage from "./pages/StrategyLabPage";
import LivePage from "./pages/LivePage";
import DigestPage from "./pages/DigestPage";
import ThemesPage from "./pages/ThemesPage";
import ThemeDeepPage from "./pages/ThemeDeepPage";
import SignalsPage from "./pages/SignalsPage";
import ReboundRadarPage from "./pages/ReboundRadarPage";
import DividendAlphaPage from "./pages/DividendAlphaPage";
import EarningsAlphaPage from "./pages/EarningsAlphaPage";
import DiscoveryScoutsPage from "./pages/DiscoveryScoutsPage";
import FundGradesPage from "./pages/FundGradesPage";
import StatsPage from "./pages/StatsPage";
import CentralityPage from "./pages/CentralityPage";
import CurveModelPage from "./pages/CurveModelPage";
import CurvesPipelinePage from "./pages/CurvesPipelinePage";
import DataQualityPage from "./pages/DataQualityPage";
import ConfigurationPage from "./pages/ConfigurationPage";
import DatabasePage from "./pages/DatabasePage";
import DocumentationPage from "./pages/DocumentationPage";
import StockReadPage from "./pages/StockReadPage";

// Layout
import AppLayout from "./components/AppLayout";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser({ email: data.email });
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("auth_token", data.access_token);
        setToken(data.access_token);
        setUser({ email });
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || "Login failed" };
      }
    } catch (error) {
      return { success: false, error: "Connection error" };
    }
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/runs" element={<PipelineRunsPage />} />
                    <Route path="/funds" element={<FundExplorerPage />} />
                    <Route path="/fund-performance" element={<FundPerformancePage />} />
                    <Route path="/quarters" element={<QuarterExplorerPage />} />
                    <Route path="/buy-sell" element={<BuySellPage />} />
                    <Route path="/company-profiles" element={<CompanyProfilesPage />} />
                    <Route path="/stock-perf" element={<StockPerfPage />} />
                    <Route path="/consensus" element={<ConsensusPage />} />
                    <Route path="/alpha" element={<AlphaPage />} />
                    <Route path="/barra" element={<BarraPage />} />
                    <Route path="/factors" element={<Navigate to="/barra" replace />} />
                    <Route path="/skills" element={<SkillsPage />} />
                    <Route path="/kensys-targets" element={<KensysTargetsPage />} />
                    <Route path="/nowcasting" element={<NowcastingPage />} />
                    <Route path="/entry-anchor" element={<EntryAnchorPage />} />
                    <Route path="/stock-read" element={<StockReadPage />} />
                    <Route path="/stock-read/:symbol" element={<StockReadPage />} />
                    <Route path="/kfm" element={<KfmMockupPage />} />
                    <Route path="/hidden-gems" element={<HiddenGemsPage />} />
                    <Route path="/ls-book" element={<LSBookPage />} />
                    <Route path="/strategy-lab" element={<StrategyLabPage />} />
                    <Route path="/live" element={<LivePage />} />
                    <Route path="/signals" element={<SignalsPage />} />
                    <Route path="/rebound-radar" element={<ReboundRadarPage />} />
                    <Route path="/earnings-alpha" element={<EarningsAlphaPage />} />
                    <Route path="/dividend-alpha" element={<DividendAlphaPage />} />
                    <Route path="/discovery-scouts" element={<DiscoveryScoutsPage />} />
                    <Route path="/fund-grades" element={<FundGradesPage />} />
                    <Route path="/digest" element={<DigestPage />} />
                    <Route path="/thematic" element={<ThemesPage />} />
                    <Route path="/thematic/:themeId" element={<ThemeDeepPage />} />
                    <Route path="/strategies" element={<Navigate to="/live" replace />} />
                    <Route path="/stats" element={<StatsPage />} />
                    <Route path="/centrality" element={<CentralityPage />} />
                    <Route path="/curve-model" element={<CurveModelPage />} />
                    <Route path="/curves-pipeline" element={<CurvesPipelinePage />} />
                    <Route path="/data-quality" element={<DataQualityPage />} />
                    <Route path="/config" element={<ConfigurationPage />} />
                    <Route path="/database" element={<DatabasePage />} />
                    <Route path="/docs" element={<DocumentationPage />} />
                  </Routes>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
